import { Link, type LinkComponentProps, useLocation } from '@tanstack/react-router';
import { cn } from '@tempo-io/sui-class-utils';
import {
  AccountBalance,
  Badge,
  CalendarToday,
  ContentPaste,
  CreditCard,
  Hub,
  Inventory2,
  LocationOn,
  Lock,
  ManageAccounts,
  MoveToInbox,
  Notifications,
  PostAdd,
  Psychology,
  Timer,
  Tune,
} from '@tempo-io/sui-icons';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgName } from '@/lib/hooks/common/useOrgName';
import { useFlag } from '@/lib/hooks/feature-flags/useFlag';
import { usePermissions } from '@/lib/queries/access/usePermissions';

interface SettingsLinkProps extends LinkComponentProps {
  icon: ReactNode;
  label: string;
  isActive?: boolean;
}

const SettingsLink = ({
  icon,
  label,
  activeOptions,
  isActive = false,
  ...props
}: SettingsLinkProps) => {
  const orgName = useOrgName();
  return (
    <Link
      {...props}
      data-testid={`settingsLink-${props.to}`}
      params={{ org: orgName }}
      className={cn(
        'flex items-center gap-2 rounded px-2 py-2 transition-colors text-sm',
        'hover:bg-bg-neutral-subtle-hovered',
        '[&.active]:bg-bg-neutral-subtle-pressed',
        isActive && 'bg-bg-neutral-subtle',
      )}
      activeOptions={{ exact: true, ...activeOptions }}
    >
      {icon} <span className="truncate">{label}</span>
    </Link>
  );
};

const SettingsGroupContainer = ({
  categoryName,
  children,
}: {
  categoryName: string;
  children: ReactNode;
}) => {
  return (
    <div className="flex flex-col gap-2 ">
      <span data-testid={`settings-group-${categoryName}`} className="text-sm">
        {categoryName}
      </span>
      {children}
    </div>
  );
};

const useIsSettingsPageActive = (page: 'user-management' | 'capacity') => {
  const location = useLocation();
  return location.pathname.includes('/settings') && location.pathname.includes(`/settings/${page}`);
};

export const SettingsPicker = () => {
  const { t } = useTranslation();
  const { data: permissions } = usePermissions();
  const itemStoreContainers = useFlag('itemStoreContainers');
  const capacityLocations = useFlag('capacityLocations');
  const trialSubscriptionEnabled = useFlag('trialSubscriptionEnabled');
  const notificationsEnabled = useFlag('notificationsEnabled');
  const isUserManagementActive = useIsSettingsPageActive('user-management');
  const isCapacityActive = useIsSettingsPageActive('capacity');

  const hasAdminPermission = permissions?.edit_organization ?? false;

  return (
    <div className="flex flex-col gap-2">
      <SettingsGroupContainer categoryName={t('common.settings.workspace')}>
        {itemStoreContainers && (
          <SettingsLink
            to="/org/$org/settings/boxes"
            icon={<Inventory2 />}
            label={t('common.settings.boxes.boxes')}
          />
        )}
        <SettingsLink
          to="/org/$org/settings/integrations"
          icon={<Hub />}
          label={t('common.settings.integrations.integrations')}
        />
        <SettingsLink
          to="/org/$org/settings/item-types"
          icon={<PostAdd />}
          label={t('common.settings.itemTypes.itemTypes')}
        />
        <SettingsLink
          to="/org/$org/settings/capacity"
          isActive={isCapacityActive}
          icon={<CalendarToday />}
          label={t('common.settings.capacity.capacity')}
        />
        <SettingsLink
          to="/org/$org/settings/effort-estimation"
          icon={<Timer />}
          label={t('common.settings.effortEstimation.effortEstimation')}
        />
        <SettingsLink
          to="/org/$org/settings/custom-item-status"
          icon={<ContentPaste />}
          label={t('customItemStatus.heading')}
        />

        <SettingsLink
          to="/org/$org/settings/custom-fields"
          icon={<Tune />}
          label={t('common.settings.customFields.customFields')}
        />
      </SettingsGroupContainer>

      <SettingsGroupContainer categoryName={t('common.settings.manageStaff')}>
        {capacityLocations && (
          <SettingsLink
            to="/org/$org/settings/location"
            icon={<LocationOn />}
            label={t('common.settings.location.location')}
          />
        )}
        <SettingsLink
          to="/org/$org/settings/roles"
          icon={<Badge />}
          label={t('common.settings.roles')}
        />
        <SettingsLink
          to="/org/$org/settings/skills"
          icon={<Psychology />}
          label={t('common.settings.skills')}
        />
      </SettingsGroupContainer>

      {notificationsEnabled && (
        <SettingsGroupContainer categoryName={t('common.settings.personal')}>
          <SettingsLink
            to="/org/$org/settings/notifications"
            icon={<Notifications />}
            label={t('common.settings.notifications.notifications')}
          />
          <SettingsLink
            to="/org/$org/settings/notifications/subscriptions"
            icon={<MoveToInbox />}
            label={t('common.settings.notifications.subscriptions')}
          />
        </SettingsGroupContainer>
      )}

      {hasAdminPermission && (
        <SettingsGroupContainer categoryName={t('common.settings.administration')}>
          <SettingsLink
            to="/org/$org/settings/organization"
            icon={<AccountBalance />}
            label={t('common.settings.organization')}
          />
          <SettingsLink
            to="/org/$org/settings/connections"
            icon={<Lock />}
            label={t('common.settings.connections.connections')}
          />
          <SettingsLink
            to="/org/$org/settings/user-management/users"
            isActive={isUserManagementActive}
            icon={<ManageAccounts />}
            label={t('userManagement.title')}
          />
          {trialSubscriptionEnabled && (
            <SettingsLink
              to="/org/$org/settings/subscriptions"
              icon={<CreditCard />}
              label={t('common.settings.subscriptions.subscriptions')}
            />
          )}
        </SettingsGroupContainer>
      )}
    </div>
  );
};
